/**
 * klubb-cart-reconcile (D45) — reconcile the cart when the active club changes.
 *
 * When a member switches club, the cart must end up holding only items the new
 * club can buy, re-priced for that club:
 *   1. Ineligible lines are removed (with their Trykk / package dependents).
 *   2. Remaining lines are re-stamped _klubb_club = new slug so the klubbpris
 *      discount Function recomputes against the new club on the next render.
 *
 * Eligibility contract (see also Q20): a line carries `_eligible_clubs`, stamped
 * at add-time from the product's traede.eligible_for_clubs metafield. A line is
 * eligible for club C when that token list is EMPTY (a general / "resten av
 * butikken" product, eligible for everyone) OR it contains one of C's
 * identifiers. The matcher is deliberately tolerant — it accepts the club slug,
 * short_name, or crest_initials (case-insensitive) — because the exact format
 * of eligible_for_clubs is not yet standardised (recommend the slug, e.g. "bsk").
 * The feature is inert until products actually carry eligibility tokens.
 *
 * Pure helpers are exported for unit tests; the orchestrator takes its IO as
 * injected dependencies so it can be tested without fetch/DOM. The browser
 * wiring (the styled confirm dialog + real cart calls) lives in the picker.
 */

export function parseEligible(raw) {
  if (raw == null) return [];
  return String(raw)
    .toLowerCase()
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function lineEligibleForClub(eligibleRaw, club) {
  const tokens = parseEligible(eligibleRaw);
  if (tokens.length === 0) return true; // general — eligible for everyone
  if (!club) return false; // club-scoped line with no active club
  const ids = [club.slug, club.short_name, club.crest_initials]
    .filter(Boolean)
    .map((s) => String(s).toLowerCase());
  return tokens.some((t) => ids.indexOf(t) !== -1);
}

// The ineligible PRIMARY lines for the target club, each with a title for the
// confirm dialog. Trykk dependents (carrying _print_for) are skipped — they
// cascade with their garment via expandRemovalKeys.
export function computeRemovals(items, club) {
  const removals = [];
  (items || []).forEach((it) => {
    const props = it.properties || {};
    if (props._print_for) return; // a Trykk charge line — follows its garment
    if (!lineEligibleForClub(props._eligible_clubs, club)) {
      removals.push({ key: it.key, title: it.product_title || it.title || '', eligible: props._eligible_clubs || '' });
    }
  });
  return removals;
}

// Expand a set of primary line keys to every dependent that must go with them:
// Trykk lines sharing a removed garment's _print_group, and package lines
// sharing a removed line's _bundle_id (mirrors the cart's existing cascade).
export function expandRemovalKeys(items, primaryKeys) {
  const keySet = new Set(primaryKeys);
  const groups = new Set();
  const bundles = new Set();
  (items || []).forEach((it) => {
    if (keySet.has(it.key)) {
      const p = it.properties || {};
      if (p._print_group) groups.add(p._print_group);
      if (p._bundle_id) bundles.add(p._bundle_id);
    }
  });
  (items || []).forEach((it) => {
    const p = it.properties || {};
    if ((p._print_group && groups.has(p._print_group)) || (p._bundle_id && bundles.has(p._bundle_id))) {
      keySet.add(it.key);
    }
  });
  return Array.from(keySet);
}

/**
 * Orchestrate a club change. IO is injected for testability:
 *   io.getCart()                  → Promise<{ items }>
 *   io.confirmRemoval(removals, club) → Promise<boolean>  (the styled dialog)
 *   io.removeLines(keys)          → Promise   (atomic /cart/update.js → 0)
 *   io.restampLines(lines, slug)  → Promise   (per-line /cart/change.js)
 *
 * Returns { switched } — false means the member cancelled and nothing changed.
 */
export async function reconcileClubChange(club, io) {
  const cart = await io.getCart();
  const items = (cart && cart.items) || [];
  const removals = computeRemovals(items, club);

  if (removals.length > 0) {
    const ok = await io.confirmRemoval(removals, club);
    if (!ok) return { switched: false };
    const removeKeys = expandRemovalKeys(items, removals.map((r) => r.key));
    await io.removeLines(removeKeys);
    const remaining = items.filter((it) => removeKeys.indexOf(it.key) === -1);
    await io.restampLines(remaining, club.slug);
    return { switched: true };
  }

  // Nothing to remove — still re-stamp so klubbpris recomputes for the new club.
  await io.restampLines(items, club.slug);
  return { switched: true };
}

// Resolve a club object (slug/short_name/crest_initials/catalog handle) from the
// clubs JSON the layout injects, so the picker only has to pass a slug.
export function findClub(doc, slug) {
  const d = doc || (typeof document !== 'undefined' ? document : null);
  const el = d && d.getElementById('klubb-clubs-data');
  if (!el) return null;
  try {
    const clubs = JSON.parse(el.textContent || '[]');
    if (!Array.isArray(clubs)) return null;
    for (let i = 0; i < clubs.length; i++) {
      if (clubs[i] && clubs[i].slug === slug) return clubs[i];
    }
  } catch (e) { /* malformed — degrade */ }
  return null;
}

/* ============================================================
 * Browser glue. Exposes window.klubbCartReconcile.run(slug) for the picker;
 * provides the real cart IO + the styled confirm dialog. Kept out of the pure
 * helpers above so the orchestrator stays unit-testable with injected IO.
 * ============================================================ */
(function () {
  'use strict';
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => { if (!r.ok) throw new Error('cart call failed: ' + r.status); return r.json(); });
  }

  // Styled confirm dialog rendered by the picker snippet. Falls back to a native
  // confirm if the markup isn't present, so reconciliation never silently skips.
  function confirmRemoval(removals, club) {
    return new Promise((resolve) => {
      const dialog = document.querySelector('[data-klubb-reconcile-dialog]');
      if (!dialog) {
        const names = removals.map((r) => '• ' + r.title).join('\n');
        resolve(window.confirm('Bytter du klubb, fjernes disse varene:\n' + names + '\n\nFortsette?'));
        return;
      }
      const nameEl = dialog.querySelector('[data-klubb-reconcile-club]');
      if (nameEl) nameEl.textContent = club.name || club.short_name || club.slug || '';
      const list = dialog.querySelector('[data-klubb-reconcile-list]');
      if (list) {
        list.textContent = '';
        removals.forEach((r) => {
          const li = document.createElement('li');
          li.textContent = r.title;
          list.appendChild(li);
        });
      }
      const confirmBtn = dialog.querySelector('[data-klubb-reconcile-confirm]');
      const cancelBtn = dialog.querySelector('[data-klubb-reconcile-cancel]');
      function cleanup(result) {
        dialog.hidden = true;
        document.documentElement.style.overflow = '';
        if (confirmBtn) confirmBtn.removeEventListener('click', onConfirm);
        if (cancelBtn) cancelBtn.removeEventListener('click', onCancel);
        resolve(result);
      }
      function onConfirm() { cleanup(true); }
      function onCancel() { cleanup(false); }
      if (confirmBtn) confirmBtn.addEventListener('click', onConfirm);
      if (cancelBtn) cancelBtn.addEventListener('click', onCancel);
      dialog.hidden = false;
      document.documentElement.style.overflow = 'hidden';
    });
  }

  function run(slug) {
    const club = findClub(document, slug) || { slug: slug };
    const io = {
      getCart: function () {
        return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      },
      confirmRemoval: confirmRemoval,
      removeLines: function (keys) {
        if (!keys || keys.length === 0) return Promise.resolve();
        const updates = {};
        keys.forEach((k) => { updates[k] = 0; });
        return postJSON('/cart/update.js', { updates: updates });
      },
      restampLines: function (lines, newSlug) {
        // Sequential /cart/change.js — each call rewrites that line's properties
        // (Shopify replaces, not merges), so we resend the existing props with
        // _klubb_club swapped. Skip lines already on the target club.
        return (lines || []).reduce(function (chain, line) {
          return chain.then(function () {
            const props = Object.assign({}, line.properties || {});
            if (props._klubb_club === newSlug) return null;
            props._klubb_club = newSlug;
            return postJSON('/cart/change.js', { id: line.key, properties: props });
          });
        }, Promise.resolve());
      },
    };
    return reconcileClubChange(club, io);
  }

  window.klubbCartReconcile = { run: run, findClub: findClub, lineEligibleForClub: lineEligibleForClub };
})();
